import { ArrowBack, ArrowForward, CompareArrows, SportsSoccer, Square } from '@mui/icons-material';
import { Box, TableRow, Tooltip } from '@mui/material';
import PropTypes from 'prop-types';
import { useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Context } from '../../service/Context';
import { capitalizar } from '../../utils/UtilesComun';
import BotonListasConJugador from '../botones/BotonListasConJugador';
import BotonObservacionesLista from '../botones/BotonObservacionesLista';
import { ButtonTableApp, TableCellApp, TableModuleApp, TypographyTableApp } from '../custom/CustomComponents';

/**
 * Columnas de la tabla de jugadores de un partido.
 */
const columns = [
  { field: 'dorsal', label: 'Nº', width: '8%', align: 'center', tooltip: 'Dorsal', sx: { textAlign: 'center' } },
  { field: 'nombreJugador', label: 'Nombre', width: '46%', tooltip: 'Nombre del jugador' },
  {
    field: 'seguimiento',
    label: 'Seg.',
    width: '13%',
    align: 'center',
    tooltip: 'Añadir a listas de scouting o registrar observaciones',
    sx: { textAlign: 'center', px: 0.5 },
  },
  {
    field: 'goles',
    tooltip: 'Goles marcados',
    width: '11%',
    align: 'center',
    icon: SportsSoccer,
    sx: { borderLeft: '1px solid #e2e8f0', textAlign: 'center' },
  },
  {
    field: 'tarjetas',
    tooltip: 'Tarjetas recibidas',
    width: '11%',
    align: 'center',
    icon: Square,
    color: '#eab308',
    sx: { textAlign: 'center' },
  },
  {
    field: 'cambios',
    tooltip: 'Sustituciones',
    width: '11%',
    align: 'center',
    icon: CompareArrows,
    sx: { textAlign: 'center' },
  },
];

// Las columnas no son ordenables
for (let i = 0; i < columns.length; i++) {
  columns[i].sortable = false;
}

/**
 * Fila de la tabla de jugadores de un partido.
 */
const FilaJugadorPartido = ({ jugadorPartido, columns, listasJugador, handleListasChange, onUpdate }) => {
  const navigate = useNavigate();
  const enLista = Boolean(listasJugador && listasJugador.length > 0);

  function handleClickJugador(id) {
    navigate(`/jugador/${id}`);
  }

  return (
    <TableRow
      key={jugadorPartido.idJugador}
      hover
      sx={{
        transition: 'background-color 0.15s ease-in-out',
        '&:hover': {
          backgroundColor: '#e8edf2 !important',
        },
      }}
    >
      {columns.map((col) => {
        if (col.field === 'dorsal') {
          return (
            <TableCellApp key={col.field} sx={{ textAlign: 'center', py: 0.5, px: '2px !important' }}>
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minWidth: 22,
                  height: 22,
                  borderRadius: '5px',
                  backgroundColor: '#f1f5f9',
                  color: '#475569',
                  border: '1px solid #e2e8f0',
                  fontWeight: 700,
                  fontSize: '0.74rem',
                  lineHeight: 1,
                }}
              >
                {jugadorPartido.dorsal || '-'}
              </Box>
            </TableCellApp>
          );
        }

        if (col.field === 'nombreJugador') {
          return (
            <TypographyTableApp key={col.field}>
              <ButtonTableApp
                enLista={enLista}
                onClick={() => handleClickJugador(jugadorPartido.idJugador)}
                text={capitalizar(jugadorPartido.nombreJugador)}
                tooltip={capitalizar(jugadorPartido.nombreJugador)}
              />
            </TypographyTableApp>
          );
        }

        if (col.field === 'seguimiento') {
          return (
            <TableCellApp
              key={col.field}
              sx={{
                py: 0.5,
                px: 0.25,
                textAlign: 'center',
                width: col.width,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                <BotonListasConJugador
                  idJugador={jugadorPartido.idJugador}
                  enLista={true}
                  badge={true}
                  compact={true}
                  listasConJugador={listasJugador}
                  onListasChange={handleListasChange}
                />
                <BotonObservacionesLista
                  idJugador={jugadorPartido.idJugador}
                  observaciones={jugadorPartido.observaciones}
                  badge={true}
                  compact={true}
                  onUpdate={onUpdate}
                />
              </Box>
            </TableCellApp>
          );
        }

        if (col.field === 'goles') {
          const totalGoles = (jugadorPartido.goles || 0) + (jugadorPartido.golesPenalti || 0);
          if (totalGoles === 0) {
            return <TableCellApp key={col.field} sx={{ borderLeft: '1px solid #e2e8f0', textAlign: 'center' }} />;
          }

          return (
            <TableCellApp
              key={col.field}
              sx={{ borderLeft: '1px solid #e2e8f0', textAlign: 'center', py: 0.5, px: 0.5 }}
            >
              <Box sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                <SportsSoccer sx={{ fontSize: '0.9rem', color: '#0f172a' }} />
                <Box
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minWidth: 18,
                    height: 18,
                    borderRadius: '4px',
                    backgroundColor: '#ecfdf5',
                    color: '#047857',
                    border: '1px solid #a7f3d0',
                    fontWeight: 700,
                    fontSize: '0.72rem',
                    lineHeight: 1,
                  }}
                >
                  {totalGoles}
                </Box>
                {jugadorPartido.golesPenalti > 0 && (
                  <Tooltip title={`${jugadorPartido.golesPenalti} de penalti`} arrow>
                    <Box sx={{ fontSize: '0.65rem', color: 'text.secondary', fontWeight: 600 }}>(p)</Box>
                  </Tooltip>
                )}
              </Box>
            </TableCellApp>
          );
        }

        if (col.field === 'tarjetas') {
          const tieneAmarilla = jugadorPartido.amarillas > 0;
          const tieneRoja = jugadorPartido.rojas > 0;

          if (!tieneAmarilla && !tieneRoja) {
            return <TableCellApp key={col.field} sx={{ textAlign: 'center' }} />;
          }

          return (
            <TableCellApp key={col.field} sx={{ textAlign: 'center', py: 0.5, px: 0.5 }}>
              <Box sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                {tieneAmarilla && (
                  <Tooltip title="Tarjeta Amarilla" arrow>
                    <Box
                      sx={{
                        width: 10,
                        height: 14,
                        borderRadius: '2px',
                        backgroundColor: '#facc15',
                        border: '1px solid #eab308',
                        boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
                      }}
                    />
                  </Tooltip>
                )}
                {tieneRoja && (
                  <Tooltip title="Tarjeta Roja" arrow>
                    <Box
                      sx={{
                        width: 10,
                        height: 14,
                        borderRadius: '2px',
                        backgroundColor: '#ef4444',
                        border: '1px solid #dc2626',
                        boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
                      }}
                    />
                  </Tooltip>
                )}
              </Box>
            </TableCellApp>
          );
        }

        if (col.field === 'cambios') {
          const esTitularSustituido =
            jugadorPartido.titular && jugadorPartido.minutos !== 90 && jugadorPartido.minutos > 0;
          const esSuplenteQueEntra = !jugadorPartido.titular && jugadorPartido.minutos > 0;

          if (!esTitularSustituido && !esSuplenteQueEntra) {
            return <TableCellApp key={col.field} sx={{ textAlign: 'center' }} />;
          }

          if (esTitularSustituido) {
            return (
              <TableCellApp key={col.field} sx={{ textAlign: 'center', py: 0.5, px: 0.5 }}>
                <Tooltip title={`Sustituido en el minuto ${jugadorPartido.minutos}`} arrow>
                  <Box
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 0.25,
                      px: 0.6,
                      py: 0.2,
                      borderRadius: '4px',
                      backgroundColor: '#fef2f2',
                      border: '1px solid #fecaca',
                      color: '#dc2626',
                      fontSize: '0.72rem',
                      fontWeight: 600,
                    }}
                  >
                    <ArrowBack sx={{ fontSize: '0.75rem', transform: 'rotate(180deg)' }} />
                    {jugadorPartido.minutos}&apos;
                  </Box>
                </Tooltip>
              </TableCellApp>
            );
          }

          const minutoEntrada = 90 - jugadorPartido.minutos;
          return (
            <TableCellApp key={col.field} sx={{ textAlign: 'center', py: 0.5, px: 0.5 }}>
              <Tooltip title={`Entró en el minuto ${minutoEntrada}`} arrow>
                <Box
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.25,
                    px: 0.6,
                    py: 0.2,
                    borderRadius: '4px',
                    backgroundColor: '#f0fdf4',
                    border: '1px solid #bbf7d0',
                    color: '#16a34a',
                    fontSize: '0.72rem',
                    fontWeight: 600,
                  }}
                >
                  <ArrowForward sx={{ fontSize: '0.75rem' }} />
                  {minutoEntrada}&apos;
                </Box>
              </Tooltip>
            </TableCellApp>
          );
        }

        return (
          <TypographyTableApp key={col.field} align="center" sx={{ textAlign: 'center' }}>
            {jugadorPartido[col.field]}
          </TypographyTableApp>
        );
      })}
    </TableRow>
  );
};

const JugadoresPartido = ({ jugadores, titulo, onUpdate, loading = false }) => {
  const { obtenerListasConJugador } = useContext(Context);
  const [listasCache, setListasCache] = useState({});

  const sortedJugadores = useMemo(() => {
    if (!jugadores) {
      return [];
    }
    return [...jugadores].sort((a, b) => {
      const dorsalA = parseInt(a.dorsal, 10) || 999;
      const dorsalB = parseInt(b.dorsal, 10) || 999;
      return dorsalA - dorsalB;
    });
  }, [jugadores]);

  useEffect(() => {
    async function cargarEstadoListas() {
      if (!jugadores || jugadores.length === 0) {
        return;
      }

      // Avoid re-fetching if we already have data for these players
      const idsToFetch = jugadores.map((j) => j.idJugador).filter((id) => listasCache[id] === undefined);

      if (idsToFetch.length === 0) {
        return;
      }

      const promesas = idsToFetch.map(async (id) => {
        const listas = await obtenerListasConJugador(id);
        return { id: id, listas: listas || [] };
      });

      const resultados = await Promise.all(promesas);
      if (resultados.length > 0) {
        setListasCache((prev) => {
          const next = { ...prev };
          resultados.forEach((r) => (next[r.id] = r.listas));
          return next;
        });
      }
    }
    cargarEstadoListas();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jugadores]);

  const handleListasChange = (idJugador, listas) => {
    setListasCache((prev) => ({ ...prev, [idJugador]: listas }));
  };

  if (!titulo) {
    return null;
  }

  if (!jugadores && !loading) {
    return null;
  }

  return (
    <TableModuleApp
      title={titulo}
      columns={columns}
      loading={loading}
      data={sortedJugadores}
      renderRow={(jugador) => (
        <FilaJugadorPartido
          key={jugador.idJugador}
          jugadorPartido={jugador}
          columns={columns}
          listasJugador={listasCache[jugador.idJugador] || []}
          handleListasChange={(listas) => handleListasChange(jugador.idJugador, listas)}
          onUpdate={onUpdate}
        />
      )}
    />
  );
};

JugadoresPartido.propTypes = {
  nombreEquipo: PropTypes.string,
  titulo: PropTypes.string,
  jugadores: PropTypes.arrayOf(
    PropTypes.shape({
      idJugador: PropTypes.number,
      titular: PropTypes.bool,
    })
  ),
  onUpdate: PropTypes.func,
  loading: PropTypes.bool,
};

FilaJugadorPartido.propTypes = {
  jugadorPartido: PropTypes.object,
  columns: PropTypes.array,
  listasJugador: PropTypes.array,
  handleListasChange: PropTypes.func,
  onUpdate: PropTypes.func,
};

export default JugadoresPartido;
